import Papa from 'papaparse';
import * as XLSX from 'xlsx';
import {
  detectHierarchicalContent,
  processExcelDataWithHierarchicalGrouping,
  HierarchicalParseOptions,
} from './smartCellParser';

/**
 * Clean and parse Excel formatted values, focusing on formatted numbers
 *
 * WHY THIS COMPLEX APPROACH?
 * =========================
 *
 * Excel data processing presents a fundamental dilemma:
 *
 * 1. RAW VALUES (raw: true):
 *    - Numbers: 1000 (perfect)
 *    - Dates: 44927 (Excel serial number - useless!)
 *
 * 2. FORMATTED VALUES (raw: false):
 *    - Numbers: "1,000" (formatted with commas - needs cleaning)
 *    - Dates: "2024-01-01" (human readable - perfect!)
 *
 * SOLUTION: Use raw: false + smart cleaning
 * ==========================================
 *
 * We use raw: false to get readable dates, then apply conservative cleaning
 * to convert only clearly formatted numbers back to actual numbers.
 *
 * CONSERVATIVE CRITERIA (ALL must be true):
 * ========================================
 * - Must match strict regex patterns for formatted numbers
 * - Contains only valid number characters (digits, commas, decimals, currency)
 * - Has ≤ 3 commas (reasonable for thousands separators)
 * - No consecutive commas (,,)
 * - No leading/trailing commas (,1000 or 1000,)
 * - Has ≤ 1 decimal point (rejects European format like 1.000.000)
 *
 * EXAMPLES:
 * =========
 * ✅ "1,000" → 1000 (formatted number)
 * ✅ "$1,000" → 1000 (currency prefix)
 * ✅ "1,000 CAD" → 1000 (currency suffix with space)
 * ✅ "-1,000" → -1000 (negative number with minus sign)
 * ✅ "(1,000)" → -1000 (accounting format negative)
 * ✅ "25%" → 0.25 (percentage converted to decimal)
 * ✅ "1000CAD" → "1000CAD" (no space - preserved as text)
 * ✅ "2024-01-01" → "2024-01-01" (date - preserved!)
 * ✅ "1,000 ABC" → "1,000 ABC" (text - preserved!)
 * ✅ "1.000.000" → "1.000.000" (European format - preserved!)
 * ✅ "1/1/2024" → "1/1/2024" (date - preserved!)
 *
 * This approach ensures we get readable dates while cleaning formatted numbers,
 * without false positives that would corrupt data.
 */
export const cleanExcelValue = (value: unknown): unknown => {
  // Handle strings (from raw: false or mixed data)
  if (typeof value === 'string') {
    // Trim whitespace (Excel often adds trailing spaces)
    const trimmedValue = value.trim();

    // Skip empty strings
    if (!trimmedValue) {
      return value;
    }

    // Check for percentage format: 25% = 0.25 (must end exactly with %)
    const percentageFormatRegex = /^[\d,]+\.?\d*%$/;
    if (percentageFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/%/g, '').replace(/,/g, '');
      const parsedNumber = parseFloat(cleanedValue);

      if (!isNaN(parsedNumber)) {
        return parsedNumber / 100; // Convert to decimal
      }
    }

    // Check for negative numbers with minus sign: -1,000 = -1000
    const negativeNumberRegex = /^-[\d,]+\.?\d*$/;
    if (negativeNumberRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue.replace(/,/g, '');
      const parsedNumber = parseFloat(cleanedValue);

      if (!isNaN(parsedNumber)) {
        return parsedNumber; // Already negative
      }
    }

    // Check for accounting format: (1,000) = -1000
    const accountingFormatRegex = /^\([\d,]+\.?\d*\)$/;
    if (accountingFormatRegex.test(trimmedValue)) {
      const cleanedValue = trimmedValue
        .replace(/[(),]/g, '')
        .replace(/\s/g, '');
      const parsedNumber = parseFloat(cleanedValue);

      if (!isNaN(parsedNumber)) {
        return -parsedNumber; // Negative number
      }
    }

    // Very conservative regex: only match strings that are clearly formatted numbers
    // Must start and end with digits, can have commas as thousands separators
    const cleanFormattedNumberRegex = /^[\d,]+\.?\d*$/;
    const cleanCurrencyPrefixRegex = /^[$€£¥]\s*[\d,]+\.?\d*$/;
    // Currency suffix MUST have a space before it (prevents "1000CAD" → requires "1000 CAD")
    const cleanCurrencySuffixRegex =
      /^[\d,]+\.?\d*\s+(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i;

    // Additional checks to avoid false positives
    const hasOnlyValidChars = /^[\d,.\s$€£¥A-Za-z]*$/.test(trimmedValue);
    const hasReasonableCommaCount =
      (trimmedValue.match(/,/g) || []).length <= 3; // Max 3 commas
    const noConsecutiveCommas = !/,{2,}/.test(trimmedValue);
    const noLeadingTrailingCommas = !/^,|,$/.test(trimmedValue);
    const hasOnlyOneDecimalPoint =
      (trimmedValue.match(/\./g) || []).length <= 1; // Max 1 decimal point

    if (
      (cleanFormattedNumberRegex.test(trimmedValue) ||
        cleanCurrencyPrefixRegex.test(trimmedValue) ||
        cleanCurrencySuffixRegex.test(trimmedValue)) &&
      hasOnlyValidChars &&
      hasReasonableCommaCount &&
      noConsecutiveCommas &&
      noLeadingTrailingCommas &&
      hasOnlyOneDecimalPoint
    ) {
      // Remove commas, currency symbols, and currency codes, then parse as number
      const cleanedValue = trimmedValue
        .replace(/[,$€£¥\s]/g, '')
        .replace(
          /(CAD|USD|EUR|GBP|JPY|AUD|CHF|CNY|INR|BRL|MXN|RUB|ZAR|KRW|SGD|HKD|NZD)$/i,
          '',
        );
      const parsedNumber = parseFloat(cleanedValue);

      if (!isNaN(parsedNumber)) {
        return parsedNumber;
      }
    }

    // Return the original value if it doesn't match our strict criteria
    return value;
  }

  // Return numbers and other types as-is
  return value;
};

/**
 * Filter out empty rows and columns from spreadsheet data
 */
export const filterEmptyRowsAndColumns = (data: unknown[][]): unknown[][] => {
  if (data.length === 0) return data;

  // First, filter out empty rows
  const rowsFiltered = data.filter((row) => {
    // Check if the row has any non-empty, non-null, non-undefined values
    return row.some((cell) => {
      if (cell === null || cell === undefined) {
        return false;
      }
      if (typeof cell === 'string') {
        return cell.trim().length > 0;
      }
      return true; // Numbers, booleans, etc. are considered non-empty
    });
  });

  if (rowsFiltered.length === 0) return rowsFiltered;

  // Find the maximum number of columns
  const maxColumns = Math.max(...rowsFiltered.map((row) => row.length));

  // Identify which columns are completely empty
  const columnsToKeep: number[] = [];
  for (let colIndex = 0; colIndex < maxColumns; colIndex++) {
    const hasNonEmptyCell = rowsFiltered.some((row) => {
      const cell = row[colIndex];
      if (cell === null || cell === undefined) {
        return false;
      }
      if (typeof cell === 'string') {
        return cell.trim().length > 0;
      }
      return true; // Numbers, booleans, etc. are considered non-empty
    });

    if (hasNonEmptyCell) {
      columnsToKeep.push(colIndex);
    }
  }

  // Filter out empty columns from all rows
  const result = rowsFiltered.map((row) => {
    return columnsToKeep.map((colIndex) => {
      const cell = row[colIndex];
      return cell !== undefined && cell !== null ? cell : '';
    });
  });

  return result;
};

/**
 * Sort transformed data by date then by division
 */
export const sortTransformedData = (data: unknown[][]): unknown[][] => {
  if (data.length <= 1) return data; // No data or only headers

  // Find header row (first row) and data rows
  const headers = data[0];
  const dataRows = data.slice(1);

  // Find date and division column indices
  const dateColumnIndex = headers.findIndex(
    (header) =>
      typeof header === 'string' && header.toLowerCase().includes('date'),
  );

  const divisionColumnIndex = headers.findIndex(
    (header) =>
      typeof header === 'string' && header.toLowerCase().includes('division'),
  );

  // If we can't find the columns, return original data
  if (dateColumnIndex === -1 || divisionColumnIndex === -1) {
    console.warn('Could not find date or division columns for sorting');
    return data;
  }

  // Sort the data rows
  const sortedDataRows = [...dataRows].sort((a, b) => {
    // First sort by date
    const dateA = a[dateColumnIndex];
    const dateB = b[dateColumnIndex];

    // Try to parse dates
    const parsedDateA = new Date(String(dateA || ''));
    const parsedDateB = new Date(String(dateB || ''));

    // Check if dates are valid
    const dateAValid = !isNaN(parsedDateA.getTime());
    const dateBValid = !isNaN(parsedDateB.getTime());

    if (dateAValid && dateBValid) {
      const dateComparison = parsedDateA.getTime() - parsedDateB.getTime();
      if (dateComparison !== 0) {
        return dateComparison;
      }
    } else if (dateAValid && !dateBValid) {
      return -1; // Valid dates come first
    } else if (!dateAValid && dateBValid) {
      return 1;
    }

    // If dates are equal or both invalid, sort by division
    const divisionA = String(a[divisionColumnIndex] || '');
    const divisionB = String(b[divisionColumnIndex] || '');

    return divisionA.localeCompare(divisionB);
  });

  // Return headers + sorted data
  return [headers, ...sortedDataRows];
};

/**
 * Generate CSV string from spreadsheet data
 */
export const generateCsvString = (data: unknown[][]): string => {
  return Papa.unparse(data);
};

/**
 * Generate session ID for tracking transformations
 */
export const generateSessionId = (): string => {
  return Date.now().toString() + Math.random().toString(36).substr(2, 9);
};

/**
 * Clean all values in a 2D array using cleanExcelValue
 */
const cleanDataValues = (data: unknown[][]): unknown[][] => {
  return data.map((row) => row.map((cell) => cleanExcelValue(cell)));
};

/**
 * Unified Excel sheet processing function
 * Handles sheet_to_json conversion, cleaning, filtering, and hierarchical processing
 */
export const processExcelSheet = (
  sheet: XLSX.WorkSheet,
  options: {
    hierarchicalOptions?: Partial<HierarchicalParseOptions>;
    sheetName?: string;
  } = {},
) => {
  // Convert sheet to JSON with formatted values for proper string cleaning
  const rawJsonData = XLSX.utils.sheet_to_json(sheet, {
    header: 1,
    defval: '',
    raw: false, // Use formatted values, then clean formatted numbers
  }) as unknown[][];

  // Process the data using existing pipeline
  const result = processSheetData(rawJsonData, options.hierarchicalOptions);

  return {
    ...result,
    sheetName: options.sheetName,
    rawData: rawJsonData, // Include raw data for debugging if needed
  };
};

/**
 * Process sheet data with hierarchical grouping if detected
 */
export const processSheetData = (
  rawJsonData: unknown[][],
  hierarchicalOptions: Partial<{
    enableHierarchicalGrouping: boolean;
    parentColumnSuffix: string;
    childColumnSuffix: string;
  }> = {},
) => {
  // First clean all values to handle Excel dates/numbers properly
  const cleanedData = cleanDataValues(rawJsonData);

  // Then filter out empty rows and columns
  const filteredData = filterEmptyRowsAndColumns(cleanedData);

  // Apply hierarchical grouping if detected AND enabled
  const hierarchicalAnalysis = detectHierarchicalContent(
    filteredData,
    hierarchicalOptions,
  );

  let finalData = filteredData;
  let hierarchyReport = null;

  // Only apply grouping if both detection finds content AND caller wants it enabled
  if (
    hierarchicalAnalysis.hasHierarchicalContent &&
    (hierarchicalOptions?.enableHierarchicalGrouping ?? true)
  ) {
    const options = {
      enableHierarchicalGrouping: true,
      parentColumnSuffix: '',
      childColumnSuffix: ' sub col a',
      ...hierarchicalOptions,
    };

    const result = processExcelDataWithHierarchicalGrouping(
      filteredData,
      options,
    );
    finalData = result.processedData;
    hierarchyReport = result.hierarchyReport;
  }

  return {
    processedData: finalData,
    hierarchyReport,
    hierarchicalAnalysis,
  };
};

/**
 * Generate AI message for hierarchical data transformation
 */
export const generateHierarchicalMessage = (hierarchyReport: {
  applied: boolean;
  parentChildPairs: Array<{
    parentValue: string;
    childCount: number;
  }>;
}): string | null => {
  if (!hierarchyReport.applied) {
    return null;
  }

  let message = `🏗️ Hierarchical data structure detected and reorganized!\n\n`;
  message += `Found ${hierarchyReport.parentChildPairs.length} parent-child group(s):\n`;
  message += hierarchyReport.parentChildPairs
    .slice(0, 5) // Limit to first 5 for readability
    .map((pair) => `• "${pair.parentValue}" (${pair.childCount} items)`)
    .join('\n');

  if (hierarchyReport.parentChildPairs.length > 5) {
    message += `\n• ... and ${
      hierarchyReport.parentChildPairs.length - 5
    } more groups`;
  }

  message += `\n\nAdded a category column to preserve parent-child relationships for better data analysis and processing.`;

  return message;
};

/**
 * Apply currency conversion to transformed data
 *
 * Takes data with a "Value" column and:
 * 1. Renames "Value" to "Value {sourceCurrency}"
 * 2. Creates new "Value" column with converted values
 *
 * @param data - 2D array with header row containing "Value" column
 * @param conversionRate - Rate to multiply values by (e.g., 1.2 for USD -> CAD)
 * @param sourceCurrency - Source currency code (e.g., "USD")
 * @param valueColumnName - Name of the value column to convert (defaults to "Value")
 * @returns New 2D array with converted values
 */
export const applyCurrencyConversion = (
  data: unknown[][],
  conversionRate: number,
  sourceCurrency: string,
  valueColumnName = 'Value',
  targetCurrency = 'CAD',
): unknown[][] => {
  if (!data || data.length === 0) {
    return data;
  }

  const headerRow = data[0] as unknown[];

  // Check if conversion has already been applied
  const alreadyConverted = headerRow.some(
    (cell) =>
      String(cell || '').trim() === `${valueColumnName} ${sourceCurrency}` ||
      String(cell || '').trim() === `Conversion Rate to ${targetCurrency}`,
  );

  if (alreadyConverted) {
    console.warn(
      `Currency conversion: Data has already been converted (found "${valueColumnName} ${sourceCurrency}" or "Conversion Rate to ${targetCurrency}" column)`,
    );
    return data; // Return original data if already converted
  }

  const valueIdx = headerRow.findIndex(
    (cell) => String(cell || '').trim() === valueColumnName,
  );

  if (valueIdx === -1) {
    console.warn(
      `Currency conversion: "${valueColumnName}" column not found in data`,
    );
    return data; // Return original data if Value column not found
  }

  // Create new data with renamed column, conversion rate column, and converted values
  return data.map((row, rowIndex) => {
    if (rowIndex === 0) {
      // Header row: rename Value column, add conversion rate column, and add new Value column
      const newHeader = [...row];
      newHeader[valueIdx] = `${valueColumnName} ${sourceCurrency}`;
      return [
        ...newHeader,
        `Conversion Rate to ${targetCurrency}`,
        valueColumnName,
      ];
    } else {
      // Data row: keep original value, add conversion rate, add converted value (rounded to 2 decimals)
      const originalValue = row[valueIdx];
      const numericValue = parseFloat(String(originalValue ?? '0'));
      const convertedValue = isNaN(numericValue)
        ? 0
        : Math.round(numericValue * conversionRate * 100) / 100; // Round to 2 decimal places

      return [...row, conversionRate, convertedValue];
    }
  });
};
